/**
 * Command Palette item registry (TASK-104, design §3.5).
 *
 * Static index of navigation targets and quick actions surfaced inside the
 * ⌘K palette. Each item carries a stable `id`, a human label, an optional
 * keyword string for fuzzy match assists (synonyms / English-translit), and
 * either a router `path` (navigation) or an `action` discriminator handled
 * by the caller (e.g. `theme.toggle`).
 *
 * Role gating is applied at render time: admin-only items are dropped for
 * selectors and vice versa. There is no narrower super-admin tier — TASK-457
 * (CEO 2026-07-28) made `admin` and `super_admin` equivalent.
 *
 * Server-side fuzzy search across tracks / accounts / venues / presets /
 * selectors / promos (design §3.5 groups 2–7) is deferred to a follow-up
 * task that wires `/admin/search` — for v0.1 the palette ships with local
 * routing + quick-action coverage only.
 */

import {
  IconBell,
  IconBuildingStore,
  IconChartLine,
  IconHistory,
  IconHome,
  IconLayoutDashboard,
  IconMessage,
  IconMusic,
  IconPlaylist,
  IconPlus,
  IconSettings,
  IconTag,
  IconTicket,
  IconUpload,
  IconUsers,
  IconCash,
  IconUserPlus,
  type Icon,
} from '@tabler/icons-react';

export type AudienceRole = 'admin' | 'selector';

export interface CommandPaletteItem {
  id: string;
  label: string;
  /** Optional helper text shown on the right side of the row. */
  description?: string;
  /** Additional keywords to widen substring match (English synonyms etc.). */
  keywords?: string;
  /** Tabler icon component for the leading slot. */
  icon: Icon;
  /** Group title (design §3.5 — Переходы, Действия, ...). */
  group: string;
  /** Navigation target. */
  path: string;
  /** Restrict the row to the listed roles. Defaults to all roles. */
  audience?: AudienceRole[];
}

const GROUP_NAVIGATION = 'Переходы';
const GROUP_ACTIONS = 'Действия';

const ADMIN_NAVIGATION_ITEMS: CommandPaletteItem[] = [
  {
    id: 'nav.dashboard',
    label: 'Главная',
    keywords: 'dashboard home',
    icon: IconLayoutDashboard,
    group: GROUP_NAVIGATION,
    path: '/dashboard',
    audience: ['admin'],
  },
  {
    id: 'nav.catalog',
    label: 'Каталог',
    keywords: 'tracks catalog музыка треки',
    icon: IconMusic,
    group: GROUP_NAVIGATION,
    path: '/catalog',
    audience: ['admin'],
  },
  {
    id: 'nav.presets',
    label: 'Подборки',
    keywords: 'presets плейлисты',
    icon: IconPlaylist,
    group: GROUP_NAVIGATION,
    path: '/presets',
    audience: ['admin'],
  },
  {
    id: 'nav.tag-dictionary',
    label: 'Словарь тегов',
    keywords: 'tags теги dictionary',
    icon: IconTag,
    group: GROUP_NAVIGATION,
    path: '/tag-dictionary',
    audience: ['admin'],
  },
  {
    id: 'nav.accounts',
    label: 'Аккаунты',
    keywords: 'clients clients accounts заведения',
    icon: IconBuildingStore,
    group: GROUP_NAVIGATION,
    path: '/accounts',
    audience: ['admin'],
  },
  {
    id: 'nav.selectors',
    label: 'Селекторы',
    keywords: 'selectors кураторы',
    icon: IconUsers,
    group: GROUP_NAVIGATION,
    path: '/selectors',
    audience: ['admin'],
  },
  {
    id: 'nav.promos',
    label: 'Промокоды',
    keywords: 'promo codes coupons скидки',
    icon: IconTicket,
    group: GROUP_NAVIGATION,
    path: '/promos',
    audience: ['admin'],
  },
  {
    id: 'nav.tickets',
    label: 'Обращения',
    keywords: 'tickets support обращения тикеты',
    icon: IconMessage,
    group: GROUP_NAVIGATION,
    path: '/tickets',
    audience: ['admin'],
  },
  {
    id: 'nav.analytics',
    label: 'Аналитика',
    keywords: 'analytics stats отчёты',
    icon: IconChartLine,
    group: GROUP_NAVIGATION,
    path: '/analytics',
    audience: ['admin'],
  },
  {
    id: 'nav.audit',
    label: 'Журнал аудита',
    keywords: 'audit log журнал',
    icon: IconHistory,
    group: GROUP_NAVIGATION,
    path: '/audit',
    audience: ['admin'],
  },
  {
    id: 'nav.notifications',
    label: 'Уведомления',
    keywords: 'notifications inbox',
    icon: IconBell,
    group: GROUP_NAVIGATION,
    path: '/notifications',
    audience: ['admin'],
  },
  {
    id: 'nav.settings',
    label: 'Настройки',
    keywords: 'settings preferences config',
    icon: IconSettings,
    group: GROUP_NAVIGATION,
    path: '/settings',
    audience: ['admin'],
  },
];

const SELECTOR_NAVIGATION_ITEMS: CommandPaletteItem[] = [
  {
    id: 'nav.selector.home',
    label: 'Главная',
    keywords: 'home dashboard',
    icon: IconHome,
    group: GROUP_NAVIGATION,
    path: '/selector/home',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.upload',
    label: 'Загрузка',
    keywords: 'upload загрузить',
    icon: IconUpload,
    group: GROUP_NAVIGATION,
    path: '/selector/upload',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.tracks',
    label: 'Мои треки',
    keywords: 'tracks мои',
    icon: IconMusic,
    group: GROUP_NAVIGATION,
    path: '/selector/tracks',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.presets',
    label: 'Мои подборки',
    keywords: 'presets мои плейлисты',
    icon: IconPlaylist,
    group: GROUP_NAVIGATION,
    path: '/selector/presets',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.promos',
    label: 'Мои промокоды',
    keywords: 'promo codes скидки',
    icon: IconTicket,
    group: GROUP_NAVIGATION,
    path: '/selector/promo-codes',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.payouts',
    label: 'Мои выплаты',
    keywords: 'payouts выплаты',
    icon: IconCash,
    group: GROUP_NAVIGATION,
    path: '/selector/payouts',
    audience: ['selector'],
  },
  {
    id: 'nav.selector.notifications',
    label: 'Уведомления',
    keywords: 'notifications inbox',
    icon: IconBell,
    group: GROUP_NAVIGATION,
    path: '/selector/notifications',
    audience: ['selector'],
  },
];

const ACTION_ITEMS: CommandPaletteItem[] = [
  {
    id: 'action.upload-tracks',
    label: 'Загрузить треки',
    keywords: 'upload tracks загрузка',
    icon: IconUpload,
    group: GROUP_ACTIONS,
    path: '/catalog/upload',
    audience: ['admin'],
  },
  {
    id: 'action.create-account',
    label: 'Создать аккаунт',
    keywords: 'new account client',
    icon: IconUserPlus,
    group: GROUP_ACTIONS,
    path: '/accounts/new',
    audience: ['admin'],
  },
  {
    id: 'action.create-promo',
    label: 'Создать промокод',
    keywords: 'new promo code discount',
    icon: IconPlus,
    group: GROUP_ACTIONS,
    path: '/promos/new',
    audience: ['admin'],
  },
  {
    id: 'action.open-settings',
    label: 'Открыть настройки',
    keywords: 'settings preferences',
    icon: IconSettings,
    group: GROUP_ACTIONS,
    path: '/settings',
    audience: ['admin'],
  },
  {
    id: 'action.selector-upload',
    label: 'Загрузить трек',
    keywords: 'upload',
    icon: IconUpload,
    group: GROUP_ACTIONS,
    path: '/selector/upload',
    audience: ['selector'],
  },
];

const ALL_ITEMS: readonly CommandPaletteItem[] = [
  ...ADMIN_NAVIGATION_ITEMS,
  ...SELECTOR_NAVIGATION_ITEMS,
  ...ACTION_ITEMS,
];

export interface CommandPaletteAudience {
  role: AudienceRole;
}

export function getVisibleItems(
  audience: CommandPaletteAudience,
): CommandPaletteItem[] {
  return ALL_ITEMS.filter(
    (item) => !item.audience || item.audience.includes(audience.role),
  );
}

